import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import cn from 'classnames';
import A_ChatAvatar from '@/components/A_Atoms/A_ChatAvatar/A_ChatAvatar';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import M_ChatMessage from '@/components/M_Molecules/M_ChatMessage/M_ChatMessage';
import O_ChatComposer from '@/components/O_Organisms/O_ChatComposer/O_ChatComposer';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { ChatMessage, Conversation } from '@/services/chatApi';
import { useConversationMessages } from '@/hooks/useChat';
import { getTranslation } from '@/constants/translations';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  conversationAvatar,
  conversationTitle,
  formatDaySeparator,
  isSameDay,
} from '@/utils/chat';
import styles from './O_ChatThread.module.css';

interface O_ChatThreadProps {
  conversation: Conversation;
  currentUserId?: string;
  /** Кнопка «назад» нужна только в мобильном сценарии со списком чатов. */
  onBack?: () => void;
  onOpenDetails: () => void;
  onRead?: (conversationId: string) => void;
  onMessageSent?: () => void;
}

/** Ниже этого расстояния от низа считаем, что пользователь читает последние сообщения. */
const STICK_TO_BOTTOM_THRESHOLD_PX = 120;
const LOAD_OLDER_THRESHOLD_PX = 80;

export default function O_ChatThread({
  conversation,
  currentUserId,
  onBack,
  onOpenDetails,
  onRead,
  onMessageSent,
}: O_ChatThreadProps) {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);

  const {
    messages,
    isLoading,
    isLoadingOlder,
    hasMore,
    error,
    loadOlder,
    sendMessage,
    retryMessage,
  } = useConversationMessages(conversation.id, { onRead });

  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const previousScrollHeightRef = useRef(0);
  const lastMessageKey = messages[messages.length - 1]?.id;

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    if (isLoadingOlder) {
      // Дочитывание истории не должно «утаскивать» видимую часть переписки.
      container.scrollTop =
        container.scrollHeight - previousScrollHeightRef.current;
      return;
    }
    if (isNearBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [lastMessageKey, isLoadingOlder]);

  useEffect(() => {
    isNearBottomRef.current = true;
  }, [conversation.id]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const distanceToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    isNearBottomRef.current = distanceToBottom < STICK_TO_BOTTOM_THRESHOLD_PX;

    if (
      container.scrollTop < LOAD_OLDER_THRESHOLD_PX &&
      hasMore &&
      !isLoadingOlder
    ) {
      previousScrollHeightRef.current = container.scrollHeight;
      void loadOlder();
    }
  }, [hasMore, isLoadingOlder, loadOlder]);

  const handleSend = useCallback(
    async (payload: Parameters<typeof sendMessage>[0]) => {
      isNearBottomRef.current = true;
      await sendMessage(payload);
      onMessageSent?.();
    },
    [onMessageSent, sendMessage]
  );

  const title = conversationTitle(conversation, t('chat.list.untitled'));
  const subtitle =
    conversation.kind === 'group'
      ? `${conversation.memberCount} ${t('chat.details.membersSuffix')}`
      : conversation.peer
        ? `@${conversation.peer.username}`
        : '';

  return (
    <section className={styles.wrapper}>
      <header className={styles.header}>
        {onBack && (
          <A_CircleButton
            variant='transparent'
            size='small'
            icon={<Q_Icon.BackArrowIcon width={20} height={20} fill='none' />}
            onClick={onBack}
          />
        )}
        <button
          type='button'
          className={styles.header_info}
          onClick={onOpenDetails}
        >
          <A_ChatAvatar
            title={title}
            src={conversationAvatar(conversation)}
            isGroup={conversation.kind === 'group'}
            size={40}
          />
          <span className={styles.header_text}>
            <span className={cn(styles.title, 'text-p-6')}>{title}</span>
            {subtitle && (
              <span className={cn(styles.subtitle, 'text-caption')}>
                {subtitle}
              </span>
            )}
          </span>
        </button>
      </header>

      <div className={styles.messages} ref={scrollRef} onScroll={handleScroll}>
        {isLoading ? (
          <p className={cn(styles.placeholder, 'text-caption')}>
            {t('chat.thread.loading')}
          </p>
        ) : error && messages.length === 0 ? (
          <p className={cn(styles.placeholder, 'text-caption')}>{t(error)}</p>
        ) : messages.length === 0 ? (
          <p className={cn(styles.placeholder, 'text-caption')}>
            {t('chat.thread.empty')}
          </p>
        ) : (
          messages.map((message, index) => (
            <React.Fragment key={message.clientId || message.id}>
              {needsDaySeparator(messages[index - 1], message) && (
                <div className={cn(styles.day, 'text-caption')}>
                  {formatDaySeparator(message.createdAt, language, {
                    today: t('chat.thread.today'),
                    yesterday: t('chat.thread.yesterday'),
                  })}
                </div>
              )}
              <M_ChatMessage
                message={message}
                isOwn={isOwnMessage(message, currentUserId)}
                currentUserId={currentUserId}
                showAuthor={
                  conversation.kind === 'group' &&
                  !isOwnMessage(message, currentUserId) &&
                  isFirstInGroup(messages[index - 1], message)
                }
                onRetry={retryMessage}
              />
            </React.Fragment>
          ))
        )}
      </div>

      <O_ChatComposer onSend={handleSend} />
    </section>
  );
}

/** Сообщение без отправителя — это оптимистичный черновик текущего пользователя. */
function isOwnMessage(message: ChatMessage, currentUserId?: string): boolean {
  if (message.isPending || message.isFailed) return true;
  return Boolean(currentUserId) && message.sender?.id === currentUserId;
}

function needsDaySeparator(
  previous: ChatMessage | undefined,
  current: ChatMessage
): boolean {
  return !previous || !isSameDay(previous.createdAt, current.createdAt);
}

function isFirstInGroup(
  previous: ChatMessage | undefined,
  current: ChatMessage
): boolean {
  if (!previous || previous.kind === 'system') return true;
  return previous.sender?.id !== current.sender?.id;
}
